import { Link, Navigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { db } from "@/lib/billing";
import { templateAllowed, usePackageAccess, type TemplateRecord } from "@/lib/packageAccess";

async function fetchTemplate(id: string): Promise<TemplateRecord | null> {
  const { data, error } = await db.from("site_templates").select("*").eq("id", id).maybeSingle();
  if (error) throw error;
  return data;
}

export default function TemplateDetailPage() {
  const { id } = useParams();
  const access = usePackageAccess();
  const { data: template, isLoading } = useQuery({ queryKey: ["site-template", id], queryFn: () => fetchTemplate(id!), enabled: !!id });
  if (isLoading || access.isLoading) return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>;
  if (!template) return <Navigate to="/templates" replace />;
  const allowed = access.isAdmin || templateAllowed(access.package, template);
  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-border bg-background/80 px-6 backdrop-blur-xl">
        <div><h1 className="text-lg font-semibold">{template.name}</h1><p className="text-xs text-muted-foreground">Details du template</p></div>
        <Button asChild variant="outline" size="sm"><Link to="/templates"><ArrowLeft className="mr-2 h-4 w-4" />Retour</Link></Button>
      </header>
      <main className="grid gap-6 p-6 lg:grid-cols-[1fr_360px]">
        <Card>
          {template.preview_image_url && <img src={template.preview_image_url} alt={template.name} className="h-72 w-full rounded-t-md object-cover object-top" />}
          <CardHeader><CardTitle>{template.name}</CardTitle></CardHeader>
          <CardContent className="space-y-5">
            <p className="text-sm leading-6 text-muted-foreground">{template.description}</p>
            <Section title="Fonctionnalites" items={template.features} />
            <Section title="Sections par defaut" items={template.default_sections} />
          </CardContent>
        </Card>
        <Card className="h-fit">
          <CardHeader><CardTitle className="text-base">Acces</CardTitle></CardHeader>
          <CardContent className="space-y-3 text-sm">
            <Row label="Type" value={template.template_type} />
            <Row label="Niche" value={template.target_niche ?? "General"} />
            <Row label="Langue" value={template.language} />
            <Row label="Package requis" value={template.required_package_slug ?? "Tous"} />
            <Row label="Service requis" value={template.required_service_slug ?? "Aucun"} />
            <Badge variant={allowed ? "default" : "secondary"}>{allowed ? "Disponible" : "Package requis"}</Badge>
            <Button asChild className="w-full" disabled={!allowed}><Link to={`/info?template=${template.id}`}>Utiliser ce template</Link></Button>
            {access.isAdmin && <Button asChild variant="outline" className="w-full"><Link to={`/templates/${template.id}/edit`}>Editer</Link></Button>}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

function Section({ title, items }: { title: string; items: string[] }) {
  return <div><h3 className="mb-2 text-sm font-semibold">{title}</h3><div className="flex flex-wrap gap-2">{items.map((item) => <Badge key={item} variant="outline">{item}</Badge>)}</div></div>;
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return <div className="flex justify-between gap-3"><span className="text-muted-foreground">{label}</span><strong className="text-right">{value}</strong></div>;
}
